import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import RegionalService from '../services/RegionalService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import UserAccountMenu from './common/UserAccountMenu';
import { useAuth } from '../contexts/AuthContext';
import areaService from '../services/AreaService';
import usuarioService from '../services/UsuarioService';
import { isGptClassicEnabled as hasGptClassicFeature } from '../utils/adminMenu';
import AdminRoleMenuOptions from './sidebar/AdminRoleMenuOptions';
import { navigateAdminMenuItem } from './sidebar/menuConfig';

const AdminEditArea = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { areaId } = useParams();
  const { user } = useAuth();
  const areaState = location.state?.area || {};
  const stateRegionalId = location.state?.idRegional || null;

  const activeRegional = useMemo(() => RegionalService.getActiveRegional(), []);
  const activeRegionalId = useMemo(() => stateRegionalId || RegionalService.getActiveRegionalId(), [stateRegionalId]);
  const activeRegionalName = activeRegional?.nombre || activeRegional?.name || activeRegional?.descripcion || 'Regional';
  const isGptClassicEnabled = useMemo(() => hasGptClassicFeature(RegionalService.getFeatureFlags()), []);
  const handleAdminMenuSelect = (item) => navigateAdminMenuItem(navigate, item.id);

  const [form, setForm] = useState({
    nombre: areaState.nombre || areaState.Nombre || '',
    descripcion: areaState.descripcion || areaState.Descripcion || '',
    idRegional: activeRegionalId || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [adminsDisponibles, setAdminsDisponibles] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsError, setAdminsError] = useState('');
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [adminsAsignados, setAdminsAsignados] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    if (!areaId) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [areaId, navigate]);

  useEffect(() => {
    const fetchAdmins = async () => {
      setAdminsLoading(true);
      setAdminsError('');
      try {
        const list = await usuarioService.getAdminAreaUsersPorRegional(activeRegionalId);
        setAdminsDisponibles(Array.isArray(list) ? list : []);
      } catch (error) {
        setAdminsError(error.message || 'No pudimos cargar los admins de área.');
      } finally {
        setAdminsLoading(false);
      }
    };

    if (activeRegionalId) {
      fetchAdmins();
    }
  }, [activeRegionalId]);

  useEffect(() => {
    const fetchArea = async () => {
      if (areaState && Object.keys(areaState).length > 0) return;
      if (!areaId) return;
      try {
        const a = await areaService.getAreaById(areaId);
        if (a) {
          setForm({
            nombre: a.nombre || a.Nombre || '',
            descripcion: a.descripcion || a.Descripcion || '',
            idRegional: a.idRegional || a.IdRegional || activeRegionalId || ''
          });
          const adminsFromApi = a.adminsArea || a.AdminsArea || [];
          if (Array.isArray(adminsFromApi)) {
            setAdminsAsignados(adminsFromApi);
          }
        }
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar el área.');
        setMessageType('error');
      }
    };
    fetchArea();
  }, [areaId, areaState, activeRegionalId]);

  useEffect(() => {
    const initialAdmins = areaState.adminsArea || areaState.AdminsArea || [];
    setAdminsAsignados(Array.isArray(initialAdmins) ? initialAdmins : []);
  }, [areaState]);

  useEffect(() => {
    if (activeRegionalId && !form.idRegional) {
      setForm((prev) => ({ ...prev, idRegional: activeRegionalId }));
    }
  }, [activeRegionalId, form.idRegional]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddAdmin = () => {
    if (!selectedAdminId) return;
    const admin = adminsDisponibles.find((a) => (a.id || a.Id) === selectedAdminId);
    if (!admin) return;
    if (adminsAsignados.some((a) => (a.id || a.Id) === selectedAdminId)) return;
    setAdminsAsignados((prev) => [...prev, admin]);
    setSelectedAdminId('');
  };

  const handleRemoveAdmin = (id) => {
    setAdminsAsignados((prev) => prev.filter((a) => (a.id || a.Id) !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del área.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Guardando cambios...');
    setMessageType('info');
    try {
      await areaService.updateArea(areaId, {
        nombre: form.nombre,
        descripcion: form.descripcion,
        idRegional: form.idRegional
      });
      setMessage('Área actualizada correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'areas' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos actualizar el área.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignAdmins = async () => {
    if (!areaId) return;
    const ids = adminsAsignados.map((a) => a.id || a.Id).filter(Boolean);
    if (ids.length === 0) {
      setMessage('Agrega al menos un admin de área para asociar.');
      setMessageType('error');
      return;
    }

    setIsAssigning(true);
    setMessage('Asociando admins al área...');
    setMessageType('info');
    try {
      const res = await areaService.assignAdmins(areaId, ids);
      const nuevos = res?.asociadosNuevos || res?.AsociadosNuevos || [];
      const actualizados = res?.actualizadosAdmin || res?.ActualizadosAdmin || [];
      const yaEran = res?.yaEranAdmin || res?.YaEranAdmin || [];
      const noEncontrados = res?.usuariosNoEncontrados || res?.UsuariosNoEncontrados || [];
      const noAdminArea = res?.usuariosNoAdminArea || res?.UsuariosNoAdminArea || [];

      const summaryParts = [];
      if (nuevos.length) summaryParts.push(`Asociados nuevos: ${nuevos.length}`);
      if (actualizados.length) summaryParts.push(`Actualizados a admin: ${actualizados.length}`);
      if (yaEran.length) summaryParts.push(`Ya eran admin: ${yaEran.length}`);
      if (noEncontrados.length) summaryParts.push(`No encontrados: ${noEncontrados.length}`);
      if (noAdminArea.length) summaryParts.push(`Sin rol Admin Área: ${noAdminArea.length}`);

      setMessage(`Asociación completada. ${summaryParts.join(' | ') || ''}`.trim());
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'No pudimos asociar los admins.');
      setMessageType('error');
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          <AdminRoleMenuOptions
            user={user}
            isGptClassicEnabled={isGptClassicEnabled}
            activeItemId="areas"
            onSelect={handleAdminMenuSelect}
          />
        </nav>
        <div className="sidebar-footer">
          <ActiveRegionalBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Editar área</h1>
            <p className="page-subtitle">Ajusta los datos del área en el regional {activeRegionalName}.</p>
          </div>
          <div className="header-right">
            <ActiveRegionalBadge />
            <UserAccountMenu />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-sitemap" />
                <span>Datos del área</span>
              </div>
            </div>
            <div className="card-content" style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '1.5rem' }}>
              <div>
                <p style={{ marginTop: 0, color: '#475569' }}>
                  Completa o ajusta los datos del área.
                </p>
                <form onSubmit={handleSubmit} className="prof-create-form">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Área X"
                    />
                  </div>
                  <div className="form-field">
                    <label>Descripción</label>
                    <textarea
                      value={form.descripcion}
                      onChange={(e) => handleChange('descripcion', e.target.value)}
                      placeholder="Descripción del área"
                      style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                    />
                  </div>

                  {message && (
                    <div
                      className="modal-message"
                      style={{
                        marginTop: '0.5rem',
                        borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                        background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                        color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                      }}
                    >
                      {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                      {message}
                    </div>
                  )}

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate('/admin-dashboard', { state: { tab: 'areas' } })}
                    >
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                      <span>Guardar cambios</span>
                    </button>
                  </div>
                </form>
              </div>

              <div style={{ borderLeft: '1px solid var(--gray-200)', paddingLeft: '1.25rem' }}>
                <div className="card-title" style={{ marginBottom: '0.75rem' }}>
                  <i className="fas fa-user-tie" style={{ color: '#3b82f6' }} />
                  <span>Admins de área</span>
                </div>
                <p style={{ marginTop: 0, color: '#475569', fontSize: '0.95rem' }}>
                  Selecciona los usuarios admin de área para este área. Los que ya están asociados se muestran abajo.
                </p>

                {adminsLoading && <p>Cargando admins...</p>}
                {adminsError && <p style={{ color: '#b91c1c' }}>{adminsError}</p>}

                {!adminsLoading && adminsDisponibles.length > 0 && (
                  <div className="form-field">
                    <label>Seleccionar admin</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <select
                        value={selectedAdminId}
                        onChange={(e) => setSelectedAdminId(e.target.value)}
                        style={{ flex: 1 }}
                      >
                        <option value="">Elige un admin</option>
                        {adminsDisponibles.map((ad) => (
                          <option key={ad.id || ad.Id} value={ad.id || ad.Id}>
                            {ad.displayName || ad.nombre || ad.apellido || ad.email}
                          </option>
                        ))}
                      </select>
                      <button type="button" className="btn btn-primary" onClick={handleAddAdmin}>
                        <i className="fas fa-plus" />
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1rem' }}>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#475569', fontWeight: 600 }}>Admins asignados</p>
                  {adminsAsignados.length === 0 && (
                    <p style={{ color: '#6b7280', margin: 0 }}>Aún no hay admins asignados.</p>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {adminsAsignados.map((ad) => {
                      const name = ad.displayName || `${ad.nombre || ''} ${ad.apellido || ''}`.trim() || ad.email;
                      return (
                        <div key={ad.id || ad.Id} style={{ border: '1px solid var(--gray-200)', borderRadius: '999px', padding: '0.35rem 0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: '#f8fafc' }}>
                          <i className="fas fa-user-tie" style={{ color: '#3b82f6' }} />
                          <span>{name}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveAdmin(ad.id || ad.Id)}
                            style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                          >
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: '1.25rem' }}>
                  <button type="button" className="btn btn-primary" onClick={handleAssignAdmins} disabled={isAssigning}>
                    {isAssigning ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-link" />}
                    <span>{isAssigning ? 'Asociando...' : 'Asociar admins'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEditArea;
